import type { TextFieldProps } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { DatePicker, type DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import React from "react";

type AdminDatePickerProps = Omit<DatePickerProps<Date>, "slotProps"> & {
  compact?: boolean;
  textFieldProps?: Omit<TextFieldProps, "sx">;
};

const getCalendarSurfaceSx = (theme: Theme) => ({
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: theme.shadows[4],
  overflow: "hidden",
  "& .MuiPickersCalendarHeader-label": {
    color: "text.primary",
    fontWeight: 700,
  },
  "& .MuiPickersDay-root": {
    fontWeight: 600,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    "&.Mui-selected": {
      backgroundColor: "primary.main",
      color: "primary.contrastText",
      "&:hover": {
        backgroundColor: "primary.dark",
      },
    },
  },
  "& .MuiPickersDay-today:not(.Mui-selected)": {
    borderColor: "primary.main",
  },
  "& .MuiDayCalendar-weekDayLabel": {
    color: "text.secondary",
    fontWeight: 700,
  },
  "& .MuiPickersArrowSwitcher-button": {
    color: "primary.main",
  },
});

/** Shared date picker styling for admin and operations surfaces. */
export const AdminDatePicker: React.FC<AdminDatePickerProps> = ({
  compact = false,
  textFieldProps,
  ...props
}) => (
  <DatePicker
    {...props}
    slotProps={{
      textField: {
        fullWidth: true,
        size: compact ? "small" : "medium",
        ...textFieldProps,
        sx: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        },
      },
      desktopPaper: {
        sx: getCalendarSurfaceSx,
      },
      mobilePaper: {
        sx: getCalendarSurfaceSx,
      },
    }}
  />
);
